import { UL_APP_VERSION_CHECK_INTERVAL, UL_APP_VERSION_CHECK_URL } from "@constants/generic";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface VersionContextValue {
  hasUpdate: boolean;
  reload: () => void;
  newAppVersion: string;
}

const VersionContext = createContext<VersionContextValue | undefined>(undefined);

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) throw new Error("useVersion must be used within VersionProvider");
  return context;
};

export const VersionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const [newAppVersion, setNewAppVersion] = useState<string>(__APP_VERSION__);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (__APP_VERSION__ === "0.0.0-0") {
      // Prevent fetching version in local environment
      return;
    }
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const fetchAppVersion = async (shouldStopPolling: boolean = true) => {
      try {
        const res = await fetch(UL_APP_VERSION_CHECK_URL, {
          cache: "no-store",
        });
        const appMetaData = (await res.json()) as { version: string; randomHash: string };
        const version = appMetaData.version + "-" + appMetaData.randomHash;
        if (version !== __APP_VERSION__) {
          // prevent showing popup in local
          setHasUpdate(true);
          setNewAppVersion(version);
          shouldStopPolling && stopPolling();
        } else {
          setHasUpdate(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Version check failed:", err);
      }
    };

    (async () => {
      await fetchAppVersion(false); // fetch the app-version immediately
      // Start polling for updates
      intervalRef.current = setInterval(fetchAppVersion, UL_APP_VERSION_CHECK_INTERVAL);
    })();

    return stopPolling;
  }, []);

  return (
    <VersionContext.Provider value={{ hasUpdate, reload, newAppVersion }}>
      {children}
    </VersionContext.Provider>
  );
};
